// EventTabs.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BASEAPIURL, token } from '../env';
import { Spinner } from 'react-bootstrap';
import party from '../Images/lobby/Lobby-01.webp';

export default function EventTabs() {
  const [activeTab, setActiveTab] = useState('1');
  const [tabData, setTabData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const processNotifications = useCallback((notifications) => {
    const relevantEvents = notifications.filter(item => {
      const fromDate = new Date(item.NotiFromDate);
      const toDate = new Date(item.NotiToDate);
      const today = new Date();
      return !isNaN(fromDate) && !isNaN(toDate) && toDate >= new Date(today.setHours(0,0,0,0));
    });

    relevantEvents.sort((a, b) => new Date(a.NotiFromDate) - new Date(b.NotiFromDate));
    const upcoming = relevantEvents.slice(0, 3);

    return upcoming.map((item, index) => {
      const fromDateObj = new Date(item.NotiFromDate);
      const formattedFromDate = isNaN(fromDateObj) 
        ? "Invalid Date" 
        : fromDateObj.toLocaleDateString("en-GB");

      return {
        id: String(index + 1),
        apiId: item.NotiID,
        title: item.NotiTitle || "Untitled Event",
        date: formattedFromDate,
        img: (item.NotiImages && item.NotiImages.trim() !== "") ? item.NotiImages : party,
        content: item.NotiDesc || "No description available",
        toDate: item.NotiToDate ? new Date(item.NotiToDate).toLocaleDateString("en-GB") : null,
      };
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({ tokenData: token });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(`${BASEAPIURL}/NotificationList`, requestOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const notifications = Array.isArray(result?.data) ? result.data : [];
      const formattedData = processNotifications(notifications);

      setTabData(formattedData);
      if (formattedData.length > 0) {
        setActiveTab(formattedData[0].id);
      } else {
        setActiveTab('1');
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setError(error.message);
      setTabData([]);
    } finally {
      setLoading(false);
    }
  }, [processNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  const tabButtons = useMemo(() => {
    if (tabData.length === 0) return null;

    return tabData.map((tab) => {
      const dateParts = tab.date.split('/');
      const day = dateParts[0] || '';
      const monthYear = dateParts.length > 1 ? `${dateParts[1]}/${dateParts[2] || ''}` : '';

      return (
        <button
          key={tab.id}
          className={`nav-link p-2 ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => handleTabClick(tab.id)}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-label={`${tab.title}, starting ${tab.date}`}
        >
          <span style={{ fontSize: '3.2em', fontWeight: 300, color: "#45b649" }}>
            {day}
          </span>
          <div style={{ fontSize: '1.2em', fontWeight: 400, color: "#45b649" }}>
            {monthYear}
          </div>
        </button>
      );
    });
  }, [tabData, activeTab, handleTabClick]);

  const tabContent = useMemo(() => {
    if (tabData.length === 0) return null;

    return tabData.map((tab) => (
      <div
        key={tab.id}
        className={`tab-pane fade ${activeTab === tab.id ? 'show active' : ''} shadow rounded p-0`}
        style={{ backgroundColor: "#000" }}
        role="tabpanel"
        aria-labelledby={`tab-${tab.title.replace(/\s+/g, '-').toLowerCase()}-${tab.id}`}
      >
        <div className="row g-0 h-100">
          <div className="col-md-6">
            <img
              src={tab.img}
              loading="lazy"
              alt={tab.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = party;
              }}
            />
          </div>
          <div className="col-md-6 d-flex flex-column">
            <div className="p-4 flex-grow-1">
              <h4 style={{ fontSize: '2em', fontWeight: 400, color: "#45b649" }}>
                {tab.title}
              </h4>
              {tab.toDate && (
                 <p className="text-white">
                   <small>Until: {tab.toDate}</small>
                 </p>
              )}
              <p className="text-white flex-grow-1" style={{ fontSize: '1em', fontWeight: 300 }}>
                {tab.content}
              </p>
            </div>
          </div>
        </div>
      </div>
    ));
  }, [tabData, activeTab]);

  // ✅ Hide entire component if no events (after loading)
  if (!loading && tabData.length === 0) {
    return null;
  }

  return (
    <div className="container">
      <div className="text-center" style={{ fontSize: '2.5em', fontWeight: 600, color: "#DC143C" }}>
        Upcoming Events
      </div>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading events...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-danger text-center" role="alert">
          {error}
        </div>
      )}

      {!loading && tabData.length > 0 && (
        <div className="row g-2">
          <div className="col-lg-2 col-md-12 col-sm-12">
            <div 
              className="nav flex-column nav-pills nav-pills-custom rounded-0 m-0" 
              role="tablist" 
              aria-orientation="vertical"
            >
              {tabButtons}
            </div>
          </div>

          <div className="col-lg-10 col-md-12 col-sm-12">
            <div className="tab-content h-100">
              {tabContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
